import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Pagination from "./Pagination";

const normalizeAlign = (cls) => {
  if (!cls) return "text-left";
  const map = {
    "text-start": "text-left",
    left: "text-left",
    center: "text-center",
    right: "text-right",
  };
  return map[cls] || cls;
};

const hasActions = ({
  setView,
  setEdit,
  setDelete,
  setDuplicate,
  extraActions,
}) => Boolean(setView || setEdit || setDelete || setDuplicate || extraActions);

const ACTION_COLUMN_WIDTH = 112;
const DEFAULT_COLUMN_WIDTH = 120;

const getColumnPixelWidth = (width, fallback = DEFAULT_COLUMN_WIDTH) => {
  if (typeof width === "number") return width;
  if (typeof width !== "string") return fallback;

  const value = width.trim();
  const match = value.match(/^(\d+(?:\.\d+)?)px$/);
  return match ? Number(match[1]) : fallback;
};

const isStatusColumn = (column) => {
  const key = String(column?.key || "").toLowerCase();
  return (
    key === "status" ||
    key === "trangthai" ||
    key === "trang_thai"
  );
};

const getEffectiveColumnWidth = (column) => {
  const width = getColumnPixelWidth(column?.minWidth);
  return isStatusColumn(column) ? Math.max(width, 160) : width;
};

const actionCellClassName =
  "sticky right-0 z-20 w-[112px] min-w-[112px] max-w-[112px] border-l border-gray-100 bg-white shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.35)]";

const actionHeaderClassName =
  "sticky right-0 z-30 w-[112px] min-w-[112px] max-w-[112px] whitespace-nowrap bg-[#2483F5] px-3 py-2 text-center shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.35)]";

const ActionPopover = ({ actions, onClose, buttonElement }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 160,
      });
    }
  }, [buttonElement]);

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      <div
        className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-2 min-w-[160px]"
        style={{ top: `${position.top}px`, left: `${position.left}px` }}
        onClick={(e) => e.stopPropagation()}
      >
        {actions?.map((action, index) => (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick(e);
              onClose();
            }}
            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 rounded-md flex items-center gap-3 ${
              action.color || "text-gray-700"
            }`}
            title={action.title}
          >
            {action.icon && (
              <img src={action.icon} alt="" className="w-4 h-4 shrink-0" />
            )}
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    </>,
    document.body,
  );
};

const CustomTable = ({
  columns,
  data,
  onRowClick,
  currentPage,
  setCurrentPage,
  totalPages,
  setView = true,
  setEdit = false,
  setDelete = false,
  setDuplicate = false,
  handleDelete,
  handleEdit: onEdit,
  handleDuplicate,
  basePath,

  selectable = false,
  selectedIds,
  onToggleRow,
  onToggleAll,
  rowIdKey,
  getRowId,
  extraActions,
  selectionDisabled = false,

  infinite = false,
  loadMore,
  hasMore = false,
  loadingMore = false,

  loading = false,
  loadingText = "Đang tải dữ liệu...",
}) => {
  const navigate = useNavigate();
  const headerCheckboxRef = useRef(null);
  const [openPopoverId, setOpenPopoverId] = useState(null);
  const [activeButtonElement, setActiveButtonElement] = useState(null);

  const headerScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  const handleBodyScroll = useCallback(() => {
    if (headerScrollRef.current && bodyScrollRef.current) {
      headerScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    }
  }, []);

  const selectedSet = useMemo(() => {
    if (!selectedIds) return new Set();
    return selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  }, [selectedIds]);

  const shouldRenderActions = hasActions({
    setView,
    setEdit,
    setDelete,
    setDuplicate,
    extraActions,
  });

  const tableMinWidth = useMemo(() => {
    const selectionWidth = selectable ? 48 : 0;
    const columnsWidth = columns.reduce(
      (sum, col) => sum + getEffectiveColumnWidth(col),
      0,
    );
    const actionsWidth = shouldRenderActions ? ACTION_COLUMN_WIDTH : 0;
    return selectionWidth + columnsWidth + actionsWidth;
  }, [columns, selectable, shouldRenderActions]);

  const tableStyle = useMemo(
    () => ({ minWidth: `${tableMinWidth}px` }),
    [tableMinWidth],
  );

  const getId = (item) => {
    if (typeof getRowId === "function") return getRowId(item);
    if (rowIdKey && item?.[rowIdKey] != null) return item[rowIdKey];
    return item?.nav_id ?? item?._id ?? item?.id ?? item?.key ?? item?.uuid;
  };

  const pageIds = useMemo(() => data.map(getId).filter(Boolean), [data]);

  const allSelected =
    selectable && data.length > 0 && pageIds.every((id) => selectedSet.has(id));
  const someSelected =
    selectable && data.some((item) => selectedSet.has(getId(item)));

  if (headerCheckboxRef.current) {
    headerCheckboxRef.current.indeterminate =
      selectable && !allSelected && someSelected;
  }

  // Infinite scroll
  useEffect(() => {
    if (
      !infinite ||
      !sentinelRef.current ||
      !loadMore ||
      !bodyScrollRef.current
    )
      return;

    const options = {
      root: bodyScrollRef.current,
      rootMargin: "50px",
      threshold: 0,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore) loadMore();
    }, options);

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [infinite, loadMore, hasMore, loadingMore]);

  const navigateToDetail = (id) => navigate(`${basePath}/${id}`);
  const navigateToEdit = (id) => navigate(`${basePath}/${id}/chinh-sua`);

  const safeTotalPages = Math.max(Number(totalPages) || 1, 1);
  const safeCurrentPage = Math.min(
    Math.max(Number(currentPage) || 1, 1),
    safeTotalPages,
  );
  const hasRows = data.length > 0;
  const shouldShowPagination = !infinite && hasRows && safeTotalPages > 1;

  const handlePageChange = (page) => {
    if (loading || typeof setCurrentPage !== "function") return;
    if (page === safeCurrentPage) return;
    if (page >= 1 && page <= safeTotalPages) setCurrentPage(page);
  };

  const renderColgroup = () => (
    <colgroup>
      {selectable && <col style={{ width: "48px" }} />}
      {columns.map((col) => (
        <col
          key={col.key}
          style={{ width: `${getEffectiveColumnWidth(col)}px` }}
        />
      ))}
      {shouldRenderActions && (
        <col style={{ width: `${ACTION_COLUMN_WIDTH}px` }} />
      )}
    </colgroup>
  );

  const renderActions = (item) => {
    const id = item.delete_id || item.nav_id || item._id || item.id;
    const itemId = getId(item);

    const actions = [];

    if (setView) {
      const handleView = (e) => {
        e.stopPropagation();
        if (typeof setView === "function") {
          setView(item);
          return;
        }
        if (!basePath && typeof onRowClick === "function") {
          onRowClick(item);
          return;
        }
        if (setView === true) {
          if (!id) {
            toast.error("Không tìm thấy ID bản ghi");
            return;
          }
          navigateToDetail(id);
        }
      };
      actions.push({
        icon: "/icons/eye.svg",
        label: "Xem chi tiết",
        title: "Xem chi tiết",
        onClick: handleView,
      });
    }

    if (setEdit) {
      actions.push({
        icon: "/icons/edit-black.svg",
        label: "Chỉnh sửa",
        title: "Chỉnh sửa",
        onClick: (e) => {
          e.stopPropagation();
          (onEdit || navigateToEdit)(id, item);
        },
      });
    }

    if (setDelete) {
      actions.push({
        icon: "/icons/trash-can.svg",
        label: "Xóa",
        title: "Xóa",
        color: "text-red-600",
        onClick: (e) => {
          e.stopPropagation();
          handleDelete?.(id, item);
        },
      });
    }

    if (setDuplicate) {
      actions.push({
        icon: "/icons/copy-black.svg",
        label: "Nhân bản",
        title: "Nhân bản",
        onClick: (e) => {
          e.stopPropagation();
          handleDuplicate?.(id, item);
        },
      });
    }

    if (typeof extraActions === "function") {
      const extraResult = extraActions(item);
      if (Array.isArray(extraResult)) actions.push(...extraResult);
    }

    if (actions.length === 0) {
      return <td className={`${actionCellClassName} py-2 text-center`}></td>;
    }

    if (actions.length === 1) {
      return (
        <td className={`${actionCellClassName} py-2 text-center`}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              actions[0].onClick(e);
            }}
            title={actions[0].title}
            className="mx-auto flex items-center justify-center rounded-full p-2 transition-colors hover:bg-gray-100 active:scale-95"
          >
            <img
              src={actions[0].icon}
              alt=""
              className="h-5 w-5 min-h-[20px] min-w-[20px] object-contain"
            />
          </button>
        </td>
      );
    }

    return (
      <td className={`${actionCellClassName} py-2 text-center`}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            const isOpening = openPopoverId !== itemId;
            setOpenPopoverId(isOpening ? itemId : null);
            setActiveButtonElement(isOpening ? e.currentTarget : null);
          }}
          className="rounded p-2 hover:bg-gray-100"
          title="Thao tác"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 10.8333C10.4602 10.8333 10.8333 10.4602 10.8333 10C10.8333 9.53976 10.4602 9.16667 10 9.16667C9.53976 9.16667 9.16667 9.53976 9.16667 10C9.16667 10.4602 9.53976 10.8333 10 10.8333Z"
              stroke="#6B7280"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10 5.00001C10.4602 5.00001 10.8333 4.62691 10.8333 4.16667C10.8333 3.70644 10.4602 3.33334 10 3.33334C9.53976 3.33334 9.16667 3.70644 9.16667 4.16667C9.16667 4.62691 9.53976 5.00001 10 5.00001Z"
              stroke="#6B7280"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10 16.6667C10.4602 16.6667 10.8333 16.2936 10.8333 15.8333C10.8333 15.3731 10.4602 15 10 15C9.53976 15 9.16667 15.3731 9.16667 15.8333C9.16667 16.2936 9.53976 16.6667 10 16.6667Z"
              stroke="#6B7280"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {openPopoverId === itemId && activeButtonElement && (
          <ActionPopover
            actions={actions}
            onClose={() => {
              setOpenPopoverId(null);
              setActiveButtonElement(null);
            }}
            buttonElement={activeButtonElement}
          />
        )}
      </td>
    );
  };

  const totalColumns =
    columns.length + (selectable ? 1 : 0) + (shouldRenderActions ? 1 : 0);

  return (
    <div className="bg-white rounded-[12px] border border-[#E3E3E3] flex flex-col h-full">
      {/* â”€â”€ HEADER TABLE (khÃ´ng scroll dá»c, chá»‰ sync scroll ngang) â”€â”€ */}
      <div
        ref={headerScrollRef}
        className="overflow-x-hidden flex-shrink-0 rounded-t-[12px]"
      >
        <table className="w-full table-fixed text-sm" style={tableStyle}>
          {renderColgroup()}
          <thead className="bg-blue-gradient rounded-t-[12px]">
            <tr className="border-b font-sf-semibold text-white uppercase text-sm">
              {selectable &&
                (!onToggleAll ? (
                  <th></th>
                ) : (
                  <th>
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      disabled={selectionDisabled}
                      onChange={() => onToggleAll?.(pageIds, allSelected)}
                    />
                  </th>
                ))}

              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`py-2 ${col.padding || "px-4"} ${normalizeAlign(col.align)} ${col.headerClassName || ""}`}
                >
                  {col.title}
                </th>
              ))}

              {shouldRenderActions && (
                <th className={actionHeaderClassName}>Thao tác</th>
              )}
            </tr>
          </thead>
        </table>
      </div>

      {/* â”€â”€ BODY TABLE (scroll dá»c + ngang, sync ngang lÃªn header) â”€â”€ */}
      <div
        ref={bodyScrollRef}
        onScroll={handleBodyScroll}
        className="overflow-x-auto overflow-y-auto flex-1 min-h-0"
      >
        <table className="w-full table-fixed text-sm" style={tableStyle}>
          {renderColgroup()}
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={totalColumns} className="py-12 text-center">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <span className="text-gray-500 text-sm">{loadingText}</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={totalColumns} className="py-12 text-center">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <p className="text-gray-500 text-sm">Không có dữ liệu</p>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item, index) => {
                const id = getId(item);
                const checked = selectable && id != null && selectedSet.has(id);

                return (
                  <tr
                    key={item.id || id || index}
                    className={`border-b hover:bg-blue-50 ${
                      onRowClick ? "cursor-pointer" : ""
                    }`}
                    onClick={() => {
                      if (onRowClick) onRowClick(item);
                    }}
                  >
                    {selectable && (
                      <td
                        className="px-4 text-center align-middle h-[72px] w-[48px]"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={!!checked}
                          disabled={selectionDisabled}
                          onChange={(e) => {
                            e.stopPropagation();
                            onToggleRow?.(id, item);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Chá»n hÃ ng"
                        />
                      </td>
                    )}

                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`h-[72px] px-4 py-2 whitespace-nowrap ${
                          isStatusColumn(col) || col.truncate === false
                            ? "overflow-visible"
                            : "overflow-hidden text-ellipsis"
                        } ${normalizeAlign(col.align)} ${col.color || ""} ${col.cellClassName || ""}`}
                        style={{ width: `${getEffectiveColumnWidth(col)}px` }}
                      >
                        {col.render
                          ? col.render(item[col.key], item, index)
                          : item[col.key]}
                      </td>
                    ))}

                    {shouldRenderActions && renderActions(item)}
                  </tr>
                );
              })
            )}

            {infinite && !loading && data.length > 0 && (
              <tr ref={sentinelRef}>
                <td
                  colSpan={totalColumns}
                  className="h-10 text-center text-gray-400 text-xs"
                >
                  {hasMore
                    ? "Scroll để tải thêm..."
                    : "Đã hiển thị tất cả dữ liệu"}
                </td>
              </tr>
            )}

            {infinite && loadingMore && (
              <tr>
                <td
                  colSpan={totalColumns}
                  className="py-4 text-center text-gray-500"
                >
                  <div className="h-[calc(100%-60px)] overflow-auto px-1 pr-3">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Đang tải thêm...
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex h-16 flex-shrink-0 items-center justify-between border-t border-[#E3E3E3] px-6">
        <div className="text-sm text-gray-600">
          {infinite ? (
            <>
              Hiển thị ‹ {data.length} bản ghi
              {hasMore && (
                <span className="text-blue-600 ml-2">(scroll để xem thêm)</span>
              )}
              {!hasMore && data.length > 0 && (
                <span className="text-gray-500 ml-2">- Đã hiển thị tất cả</span>
              )}
            </>
          ) : (
            `Hiển thị ${data.length} bản ghi`
          )}
        </div>
        <div className="flex min-w-0 items-center justify-end">
          {shouldShowPagination && (
            <Pagination
              currentPage={safeCurrentPage}
              totalPages={safeTotalPages}
              onPageChange={handlePageChange}
              disabled={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomTable;
